import React from "react";

import ErrorBoundary from "react-lib/apps/common/ErrorBoundary";

import CardPatientInfo from "react-lib/apps/HISV3/REG/CardPatientInfo";
import CardPatientInfoREGV4 from "react-lib/apps/HISV4/registration/CardPatientInfoREGV4";
import { PaginationV4 } from "react-lib/apps/HISV4/common/ui";
import CardMeBillingMenuContent, { MenuKey } from "react-lib/apps/MEH/CardMeBillingMenuContent";
import CardMeReportList from "react-lib/apps/MEH/CardMeReportList";
import CardPatientQueue from "react-lib/apps/MEH/CardPatientQueue";
import CardServicesQueue from "react-lib/apps/MEH/CardServicesQueue";

import type { V3Pages } from "react-lib/apps/HISV4/MainHISV4Interface";

import { MainHIS } from "./MainHIS";
import { getMeBillingProps } from "./ViewRegistration";
import { useOAProvidersV4 } from "react-lib/apps/HISV4/common/useOAProvidersV4";
import type { OADoctor } from "react-lib/apps/HISV4/common/oaProvidersV4";

/** Keep OA eligibility local to the legacy screens embedded in V4. */
const OAScopedBilling = ({ context, children }: {
  context: MainHIS; children: (doctors: OADoctor[]) => React.ReactNode;
}) => {
  const division = context.state.selectedDivision?.id;
  const providers = useOAProvidersV4(context.controller.apiToken, division);
  return (
    <React.Fragment key={division}>
      {providers.loading && <div>กำลังโหลดผู้ให้บริการของ OA…</div>}
      {providers.error && <div role="alert">{providers.error} <button onClick={providers.reload}>ลองใหม่</button></div>}
      {children(providers.doctors)}
    </React.Fragment>
  );
};

// ตารางของหน้า V3: สูงพอดีกรอบ V4 และใช้ pager ของ V4
const V3_TABLE_LAYOUT = {
  tableStyle: { height: "calc(100vh - 20.5rem - 3px)" },
  renderPagination: (pagination: {
    page: number;
    pageCount: number;
    onChange: (page: number) => void;
  }) => (
    <PaginationV4
      page={pagination.page}
      pageCount={pagination.pageCount}
      onChange={pagination.onChange}
    />
  ),
};

// หน้าที่วางในเมนูของ HIS V4
export const getHISV4Pages = (context: MainHIS): V3Pages => ({
  mePOSQueue: () => {
    const props = getMeBillingProps(context);

    return (
      <ErrorBoundary>
        <OAScopedBilling context={context}>{(doctors) => (
        <CardPatientQueue
          setProp={props.setProp}
          runSequence={props.runSequence}
          onEvent={props.onEvent}
          django={props.django}
          selectedEncounter={props.selectedEncounter}
          selectedPatient={props.selectedPatient}
          selectedDivision={props.selectedDivision}
          PatientQueueSequence={props.PatientQueueSequence}
          masterData={props.masterData}
          masterOptions={props.masterOptions}
          doctorList={doctors}
          buttonLoadCheck={props.buttonLoadCheck}
          errorMessage={props.errorMessage}
          // print list
          printListController={props.printListController}
          // scanner
          scannedDocumentController={props.scannedDocumentController}
          patientData={props.patientData}
          languageUX={props.languageUX}
          {...V3_TABLE_LAYOUT}
        />
        )}</OAScopedBilling>
      </ErrorBoundary>
    );
  },
  servicesQueue: () => {
    const props = getMeBillingProps(context);

    return (
      <ErrorBoundary>
        <CardServicesQueue
          onEvent={props.onEvent}
          phlebotomyQueue={props.phlebotomyQueue}
          skipNavigation
          themeColors
        />
      </ErrorBoundary>
    );
  },
  report: () => {
    const props = getMeBillingProps(context);

    return (
      <ErrorBoundary>
        <CardMeReportList
          setProp={props.setProp}
          runSequence={props.runSequence}
          onEvent={props.onEvent}
          ReportListSequence={props.ReportListSequence}
          selectedDivision={props.selectedDivision}
          masterData={props.masterData}
          masterOptions={props.masterOptions}
          buttonLoadCheck={props.buttonLoadCheck}
          errorMessage={props.errorMessage}
          languageUX={props.languageUX}
          {...V3_TABLE_LAYOUT}
        />
      </ErrorBoundary>
    );
  },
  patientInfo: (_arg, onSelect) => (
    <ErrorBoundary>
      <CardPatientInfo
        presentationComponent={CardPatientInfoREGV4}
        masterData={context.controller.data.masterData}
        onSavedEncounter={() => onSelect?.("encounter")}
        onCancel={() => onSelect?.("cancel")}
        onDirtyChange={(dirty: boolean) => onSelect?.(dirty ? "dirty" : "clean")}
        patient={context.state.SetPatientSequence?.patient || {}}
        onEvent={context.controller.handleEvent}
        setProp={context.controller.setProp}
        runSequence={context.runSequence}
        masterOptions={context.state.masterOptions}
        errorMessage={context.state.errorMessage}
        successMessage={context.state.successMessage}
        SetPatientSequence={context.state.SetPatientSequence}
        MergePatientSequence={context.state.MergePatientSequence}
        buttonLoadCheck={context.state.buttonLoadCheck}
        languageUX={context.props.userSelectedLanguage}
      />
    </ErrorBoundary>
  ),
  mePOSMenu: (menu, onSelectMenu) => {
    const { MeBillingSequence } = context.state;

    return (
      <ErrorBoundary>
        <OAScopedBilling context={context}>{(doctors) => (
        <CardMeBillingMenuContent
          {...getMeBillingProps(context)}
          menu={menu as MenuKey}
          onSelectMenu={(key: MenuKey) => onSelectMenu?.(key)}
          selectedCustomer={MeBillingSequence?.selectedCustomer}
          visitList={MeBillingSequence?.visitList}
          selectedVisit={MeBillingSequence?.selectedVisit}
          appointmentList={MeBillingSequence?.appointmentList}
          packageDetail={MeBillingSequence?.packageDetail || []}
          providerList={MeBillingSequence?.common?.providerList || []}
          doctorList={doctors}
        />
        )}</OAScopedBilling>
      </ErrorBoundary>
    );
  },
});
